import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const MANUAL_LOGOUT_KEY = 'auth_manual_logout';

const ERROR_ROUTES = new Set([
  '/error/401',
  '/error/403',
  '/error/500',
  '/maintenance',
  '/network-error',
]);

export function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function normalizeUsername(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : '';
}

export function getApiData(response, fallback = null) {
  const data = response?.data?.data;
  return data == null ? fallback : data;
}

export function getApiMessage(error, fallback = 'Something went wrong.') {
  return error?.response?.data?.message || error?.message || fallback;
}

export function toArray(value) {
  return Array.isArray(value) ? value : [];
}

export function toObject(value, fallback = {}) {
  return isPlainObject(value) ? value : fallback;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

function redirectToErrorPage(path) {
  if (typeof window === 'undefined') return;
  const currentPath = window.location.pathname;
  if (currentPath === path) return;
  window.location.assign(path);
}

api.interceptors.request.use(
  (config) => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (err) => Promise.reject(err)
);

api.interceptors.response.use(
  (response) => response,
  (err) => {
    if (typeof window !== 'undefined' && !err?.config?.skipGlobalErrorRedirect) {
      const status = err?.response?.status;
      const currentPath = window.location.pathname;
      const isOnLogin = currentPath === '/login';
      const isOnErrorPage = ERROR_ROUTES.has(currentPath);

      if (status === 401) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        const isManualLogout = localStorage.getItem(MANUAL_LOGOUT_KEY) === '1';
        if (!isOnLogin) redirectToErrorPage(isManualLogout ? '/login' : '/error/401');
      } else if (status === 403 && !isOnErrorPage) {
        redirectToErrorPage('/error/403');
      } else if (status === 503 && !isOnErrorPage) {
        redirectToErrorPage('/maintenance');
      } else if (status >= 500 && !isOnErrorPage) {
        redirectToErrorPage('/error/500');
      } else if (!err?.response && !isOnErrorPage) {
        redirectToErrorPage('/network-error');
      }
    }
    return Promise.reject(err);
  }
);

export default api;
